export interface ImageSequenceConfig {
	/** Total number of frames */
	frameCount: number
	/** Function to generate frame path from index (0-based) */
	getFramePath: (index: number) => string
	/** GSAP ScrollTrigger selector */
	triggerSelector: string
	/** ScrollTrigger start position */
	triggerStart?: string
	/** ScrollTrigger end position */
	triggerEnd?: string
	/** Number of frames to preload initially */
	initialPreloadCount?: number
	/** Number of nearby frames to preload during scroll */
	preloadRange?: number
}

export interface ImageSequenceReturn {
	/** Canvas ref to bind in template */
	canvasRef: Ref<HTMLCanvasElement | null>
	/** Images array */
	images: Ref<HTMLImageElement[]>
	/** Current frame index */
	currentFrame: Ref<number>
	/** Whether images are loading */
	isLoading: Ref<boolean>
	/** Initialize canvas and preload first frames */
	init: () => Promise<void>
	/** Start scroll-based animation */
	startScrollAnimation: () => void
	/** Draw specific frame */
	drawFrame: (frameIndex: number) => void
	/** Preload specific range of frames */
	preloadFrames: (start: number, end: number) => Promise<void>
}

/**
 * Composable for image sequence animation on scroll
 * Extracts common logic from About and Advantages sections
 */
export function useImageSequence(config: ImageSequenceConfig): ImageSequenceReturn {
	const { $gsap: gsap } = useNuxtApp()

	const {
		frameCount,
		getFramePath,
		triggerSelector,
		triggerStart = 'top bottom',
		triggerEnd = 'bottom top',
		initialPreloadCount = 10,
		preloadRange = 5,
	} = config

	const canvasRef = ref<HTMLCanvasElement | null>(null)
	const images = ref<HTMLImageElement[]>([])
	const currentFrame = ref(0)
	const isLoading = ref(true)

	/**
	 * Draw frame on canvas
	 */
	const drawFrame = (frameIndex: number) => {
		const canvas = canvasRef.value
		if (!canvas) return

		const ctx = canvas.getContext('2d')
		if (!ctx) return

		const img = images.value[frameIndex]
		if (img && img.complete && img.naturalWidth > 0) {
			ctx.clearRect(0, 0, canvas.width, canvas.height)
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
		}
	}

	/**
	 * Update canvas size based on image aspect ratio with DPR support
	 */
	const updateCanvasSize = (img: HTMLImageElement) => {
		const canvas = canvasRef.value
		if (!canvas) return

		const container = canvas.parentElement
		if (!container) return

		const dpr = window.devicePixelRatio || 1
		const containerWidth = container.clientWidth
		const aspectRatio = img.naturalHeight / img.naturalWidth
		const displayHeight = containerWidth * aspectRatio

		// Set canvas internal resolution (accounting for DPR for sharp rendering)
		canvas.width = containerWidth * dpr
		canvas.height = displayHeight * dpr

		// Set CSS display size
		canvas.style.width = `${containerWidth}px`
		canvas.style.height = `${displayHeight}px`
	}

	/**
	 * Load a single image and return promise
	 */
	const loadImage = (img: HTMLImageElement, src: string): Promise<HTMLImageElement> => {
		return new Promise((resolve, reject) => {
			if (img.src === src && img.complete) {
				resolve(img)
				return
			}

			img.onload = () => resolve(img)
			img.onerror = reject
			img.src = src
		})
	}

	/**
	 * Preload specific range of frames
	 */
	const preloadFrames = async (start: number, end: number): Promise<void> => {
		const promises: Promise<HTMLImageElement>[] = []

		for (let i = Math.max(0, start); i <= Math.min(frameCount - 1, end); i++) {
			const img = images.value[i]
			if (img && !img.src) {
				promises.push(loadImage(img, getFramePath(i)))
			}
		}

		await Promise.all(promises)
	}

	/**
	 * Initialize canvas and preload first frames
	 * Should be called before page render for priority loading
	 */
	const init = async (): Promise<void> => {
		const canvas = canvasRef.value
		if (!canvas) return

		isLoading.value = true

		// Create image objects for all frames
		images.value = []
		for (let i = 0; i < frameCount; i++) {
			const img = new Image()
			images.value.push(img)
		}

		// Load first frame immediately and set canvas size
		const firstImage = images.value[0]
		if (firstImage) {
			try {
				await loadImage(firstImage, getFramePath(0))
				updateCanvasSize(firstImage)
				drawFrame(0)

				// Preload initial frames in background (non-blocking)
				preloadFrames(1, initialPreloadCount)
			} catch (e) {
				console.warn('Failed to load first frame:', e)
			}
		}

		isLoading.value = false
	}

	/**
	 * Start scroll-based animation
	 */
	const startScrollAnimation = () => {
		gsap.to(currentFrame, {
			value: frameCount - 1,
			ease: 'none',
			scrollTrigger: {
				trigger: triggerSelector,
				start: triggerStart,
				end: triggerEnd,
				scrub: true,
				onUpdate: (self: any) => {
					const frameIndex = Math.floor(self.progress * (frameCount - 1))
					const currentImg = images.value[frameIndex]
					if (!currentImg) return

					// Load current frame if not loaded
					if (!currentImg.src) {
						currentImg.src = getFramePath(frameIndex)
					}

					// Preload nearby frames
					for (
						let i = Math.max(0, frameIndex - preloadRange);
						i <= Math.min(frameCount - 1, frameIndex + preloadRange);
						i++
					) {
						const img = images.value[i]
						if (img && !img.src) {
							img.src = getFramePath(i)
						}
					}

					// Draw current frame
					if (currentImg.complete && currentImg.naturalWidth > 0) {
						drawFrame(frameIndex)
					} else {
						currentImg.onload = () => drawFrame(frameIndex)
					}
				},
			},
		})
	}

	return {
		canvasRef,
		images,
		currentFrame,
		isLoading,
		init,
		startScrollAnimation,
		drawFrame,
		preloadFrames,
	}
}

/**
 * Preload image sequence frames before component mount
 * Call this in plugin or app setup for priority loading
 */
export async function preloadImageSequence(
	getFramePath: (index: number) => string,
	frameCount: number = 10
): Promise<HTMLImageElement[]> {
	const images: HTMLImageElement[] = []

	const loadPromises = Array.from({ length: frameCount }, (_, i) => {
		return new Promise<HTMLImageElement>((resolve, reject) => {
			const img = new Image()
			img.onload = () => resolve(img)
			img.onerror = reject
			img.src = getFramePath(i)
			images.push(img)
		})
	})

	await Promise.all(loadPromises)
	return images
}
